import Vue from "@/plugins"
import { NullableUserInfo, State, UserInfo, Property } from "@/models"
import {
    GET_USER_ID,
    GET_IS_ADMINISTRATOR,
    HAS_USER_INFO,
    GET_USER_INFO,
    GET_JWT_TOKEN,
    GET_CURRENT_ROUTE,
    GET_$SOCKET,
    HAS_$SOCKET,
    HAS_ROLE_FN,
    SOCKET_IS_DISPATCHER_CONNECTED,
    SOCKET_ISCONNECTED,
    SOCKET_ISSUBSCRIBED,
    GET_MAPBOX_STYLES_ID,
    GET_FORM_USER_NAME,
    GET_FORM_USER_PASS,
    GET_FORM_CHECKED,
    GET_DASHBOARD_TOTAL_VENUE,
    GET_DASHBOARD_TOTAL_BOX,
    GET_DASHBOARD_TOTAL_ONLINE_BOX,
    GET_DASHBOARD_TOTAL_OFFLINE_BOX,
    GET_DASHBOARD_TOTAL_UPDATE_REQUIRED_BOX,
    GET_MARKET_TOTAL_VENUE_FN,
    GET_MARKET_TOTAL_BOX_FN,
    GET_MARKET_TOTAL_ONLINE_BOX_FN,
    GET_MARKET_TOTAL_OFFLINE_BOX_FN,
    GET_MARKET_TOTAL_UPDATE_REQUIRED_BOX_FN,
    GET_VENUE_TOTAL_BOX_FN,
    GET_VENUE_TOTAL_ONLINE_BOX_FN,
    GET_VENUE_TOTAL_OFFLINE_BOX_FN,
    GET_VENUE_TOTAL_UPDATE_REQUIRED_BOX_FN,
    GET_IS_RESTARTING,
} from "@/store/types/getters"
import { isNull, isUndefined } from "lodash"
import { GetterTree } from "vuex"
import { UAcsLoginModuleNamespace, MAPBOX_STYLES_ID_CATEGORY } from "@/consts"

const getters: GetterTree<State, any> = {
    [GET_$SOCKET](state: State): any {
        return state.socket.$socket
    },
    [HAS_$SOCKET](state: State): boolean {
        return (
            !isNull(state.socket.$socket) && !isUndefined(state.socket.$socket)
        )
    },
    [SOCKET_ISCONNECTED](state: State): boolean {
        return state.socket.isConnected
    },
    [SOCKET_ISSUBSCRIBED](state: State): boolean {
        return state.socket.isSubscribed
    },
    [SOCKET_IS_DISPATCHER_CONNECTED](state: State): boolean {
        return state.socket.isDispatcherConnected
    },
    [HAS_ROLE_FN](state: State, getters: any): (role: string) => boolean {
        return (role: string): boolean => {
            return getters[UAcsLoginModuleNamespace + "/" + HAS_ROLE_FN](role)
        }
    },
    [GET_IS_ADMINISTRATOR](state: State, getters: any): boolean {
        return getters[HAS_ROLE_FN]("administrator")
    },
    [GET_USER_INFO](state: any, getters: any): NullableUserInfo {
        return getters[HAS_USER_INFO]
            ? (state[UAcsLoginModuleNamespace].user_info as UserInfo)
            : null
    },
    [HAS_USER_INFO](state: any, getters: any): boolean {
        if (isUndefined(state[UAcsLoginModuleNamespace])) {
            return false
        }

        const userInfo = state[UAcsLoginModuleNamespace].user_info as UserInfo
        return !isNull(userInfo.id) && !isNull(userInfo.username)
    },
    [GET_USER_ID](state: any, getters: any): string | null {
        return getters[HAS_USER_INFO]
            ? (getters[GET_USER_INFO].id as string)
            : null
    },
    [GET_JWT_TOKEN](state: any, getters: any): string | null {
        if (!getters[HAS_USER_INFO]) {
            return null
        }

        return state[UAcsLoginModuleNamespace].token as string
    },
    [GET_FORM_USER_NAME](state: any, getters: any): string | null {
        const form = (state as any)[UAcsLoginModuleNamespace].form as any

        return form.username
    },
    [GET_FORM_USER_PASS](state: any, getters: any): string | null {
        const form = (state as any)[UAcsLoginModuleNamespace].form as any

        return form.password
    },
    [GET_FORM_CHECKED](state: any, getters: any): string | null {
        const form = (state as any)[UAcsLoginModuleNamespace].form as any

        return form.is_checked
    },
    [GET_CURRENT_ROUTE](state: any): any {
        return state.Router
    },
    [GET_MAPBOX_STYLES_ID](state: any, getters: any): string {
        if (!getters[HAS_USER_INFO]) {
            return "mapbox/light-v10"
        }

        const property = getters[GET_USER_INFO].properties.find(
            (x: any) => x.category === MAPBOX_STYLES_ID_CATEGORY
        ) as Property | undefined

        return !isUndefined(property) ? property.value : "mapbox/light-v10"
    },
    [GET_IS_RESTARTING](state: State, getters: any): boolean {
        return state.isRestarting
    },
    [GET_DASHBOARD_TOTAL_VENUE](state: State): number {
        return isNull(state.statistics_counter)
            ? 0
            : state.statistics_counter.counter.total_venue
    },
    [GET_DASHBOARD_TOTAL_ONLINE_BOX](state: State): number {
        return isNull(state.statistics_counter)
            ? 0
            : state.statistics_counter.counter.total_online_box
    },
    [GET_DASHBOARD_TOTAL_OFFLINE_BOX](state: State): number {
        return isNull(state.statistics_counter)
            ? 0
            : state.statistics_counter.counter.total_offline_box
    },
    [GET_DASHBOARD_TOTAL_UPDATE_REQUIRED_BOX](state: State): number {
        return isNull(state.statistics_counter)
            ? 0
            : state.statistics_counter.counter.total_update_required_box
    },
    [GET_DASHBOARD_TOTAL_BOX](state: State, getters: any): number {
        return (
            getters[GET_DASHBOARD_TOTAL_ONLINE_BOX] +
            getters[GET_DASHBOARD_TOTAL_OFFLINE_BOX]
        )
    },
    [GET_MARKET_TOTAL_VENUE_FN](state: State): (marketId: string) => number {
        return (marketId: string) => {
            return isNull(state.statistics_counter)
                ? 0
                : isUndefined(
                      state.statistics_counter.market_counters[marketId]
                  )
                ? 0
                : state.statistics_counter.market_counters[marketId].total_venue
        }
    },
    [GET_MARKET_TOTAL_ONLINE_BOX_FN](
        state: State
    ): (marketId: string) => number {
        return (marketId: string) => {
            return isNull(state.statistics_counter)
                ? 0
                : isUndefined(
                      state.statistics_counter.market_counters[marketId]
                  )
                ? 0
                : state.statistics_counter.market_counters[marketId]
                      .total_online_box
        }
    },
    [GET_MARKET_TOTAL_OFFLINE_BOX_FN](
        state: State
    ): (marketId: string) => number {
        return (marketId: string) => {
            return isNull(state.statistics_counter)
                ? 0
                : isUndefined(
                      state.statistics_counter.market_counters[marketId]
                  )
                ? 0
                : state.statistics_counter.market_counters[marketId]
                      .total_offline_box
        }
    },
    [GET_MARKET_TOTAL_UPDATE_REQUIRED_BOX_FN](
        state: State
    ): (marketId: string) => number {
        return (marketId: string) => {
            return isNull(state.statistics_counter)
                ? 0
                : isUndefined(
                      state.statistics_counter.market_counters[marketId]
                  )
                ? 0
                : state.statistics_counter.market_counters[marketId]
                      .total_update_required_box
        }
    },
    [GET_MARKET_TOTAL_BOX_FN](
        state: State,
        getters: any
    ): (marketId: string) => number {
        return (marketId: string) => {
            return (
                getters[GET_MARKET_TOTAL_ONLINE_BOX_FN](marketId) +
                getters[GET_MARKET_TOTAL_OFFLINE_BOX_FN](marketId)
            )
        }
    },
    [GET_VENUE_TOTAL_ONLINE_BOX_FN](
        state: State,
        getters: any
    ): (marketId: string, venueId: string) => number {
        return (marketId: string, venueId: string) => {
            if (
                isNull(state.statistics_counter) ||
                isUndefined(
                    state.statistics_counter.market_counters[marketId]
                ) ||
                isUndefined(
                    state.statistics_counter.market_counters[marketId]
                        .venue_counters[venueId]
                )
            ) {
                return 0
            }

            return state.statistics_counter.market_counters[marketId]
                .venue_counters[venueId].total_online_box
        }
    },
    [GET_VENUE_TOTAL_OFFLINE_BOX_FN](
        state: State,
        getters: any
    ): (marketId: string, venueId: string) => number {
        return (marketId: string, venueId: string) => {
            if (
                isNull(state.statistics_counter) ||
                isUndefined(
                    state.statistics_counter.market_counters[marketId]
                ) ||
                isUndefined(
                    state.statistics_counter.market_counters[marketId]
                        .venue_counters[venueId]
                )
            ) {
                return 0
            }

            return state.statistics_counter.market_counters[marketId]
                .venue_counters[venueId].total_offline_box
        }
    },
    [GET_VENUE_TOTAL_UPDATE_REQUIRED_BOX_FN](
        state: State,
        getters: any
    ): (marketId: string, venueId: string) => number {
        return (marketId: string, venueId: string) => {
            if (
                isNull(state.statistics_counter) ||
                isUndefined(
                    state.statistics_counter.market_counters[marketId]
                ) ||
                isUndefined(
                    state.statistics_counter.market_counters[marketId]
                        .venue_counters[venueId]
                )
            ) {
                return 0
            }

            return state.statistics_counter.market_counters[marketId]
                .venue_counters[venueId].total_update_required_box
        }
    },

    [GET_VENUE_TOTAL_BOX_FN](
        state: State,
        getters: any
    ): (marketId: string, venueId: string) => number {
        return (marketId: string, venueId: string) => {
            return (
                getters[GET_VENUE_TOTAL_ONLINE_BOX_FN](marketId, venueId) +
                getters[GET_VENUE_TOTAL_OFFLINE_BOX_FN](marketId, venueId)
            )
        }
    },
}

export default getters
